import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useRegistrationStore } from '@/lib/store/registrationStore';
import { useQuery } from '@tanstack/react-query';
import { getBusinessTypes } from '@/lib/api/controlPlaneClient';
import { cn } from '@/lib/utils';
import { Check, Search } from 'lucide-react';
import { usePageTranslation } from '@/i18n/useTranslation';
import { authTranslations } from '@/i18n/locales/en/auth';
import { authTranslations as thAuthTranslations } from '@/i18n/locales/th/auth';

interface StepBusinessTypeProps {
  onNext: () => void;
  onSkip: () => void;
}

export function StepBusinessType({ onNext, onSkip }: StepBusinessTypeProps) {
  const { formData, updateFormData, error } = useRegistrationStore();
  const [searchQuery, setSearchQuery] = useState('');
  const t = usePageTranslation({
    en: authTranslations,
    th: thAuthTranslations,
  });

  const { data: businessTypes = [], isLoading } = useQuery({
    queryKey: ['registration', 'businessTypes'],
    queryFn: getBusinessTypes,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
  });

  const filteredTypes = businessTypes.filter((bt) =>
    bt.business_type_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (businessTypeName: string) => {
    // Toggle selection
    if (formData.businessType === businessTypeName) {
      updateFormData({ businessType: null });
    } else {
      updateFormData({ businessType: businessTypeName });
    }
  };

  const handleNext = () => {
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground md:text-center">
          {t.whatTypeOfBusiness}
        </h2>
      </div>

      <div className="space-y-4">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.searchBusinessTypes}
            className="pl-10 h-12 rounded-md"
          />
        </div>

        {/* Business type list */}
        <div className="max-h-64 overflow-y-auto space-y-2">
          {isLoading ? (
            <div className="text-center py-4 text-muted-foreground">{t.loading}</div>
          ) : filteredTypes.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">{t.noResultsFound}</div>
          ) : (
            filteredTypes.map((bt) => (
              <button
                key={bt.name}
                type="button"
                onClick={() => handleSelect(bt.name)}
                className={cn(
                  'w-full flex items-center justify-between p-4 rounded-md border transition-colors',
                  formData.businessType === bt.name
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-muted'
                )}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{bt.icon}</span>
                  <span className="font-medium text-foreground">
                    {bt.business_type_name}
                  </span>
                </div>
                {formData.businessType === bt.name && (
                  <Check className="w-5 h-5 text-primary" />
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive text-center">{error}</p>}

      <div className="flex gap-3">
        <Button onClick={onSkip} variant="outline" className="flex-1 h-12 rounded-md">
          {t.skip}
        </Button>
        <Button onClick={handleNext} className="flex-1 h-12 rounded-md">
          {t.next}
        </Button>
      </div>
    </div>
  );
}
